import { useState, useEffect, useCallback } from 'react';
import { Search, Phone, Zap, MessageSquare, User, ChevronDown, ChevronUp, ArrowLeft, ArrowRight } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

export type AnwaltKontaktModus = 'telefon' | 'direkt' | 'beide' | '';

export interface AnwaltOption {
  id: string;
  name: string;
  kanzlei: string;
}

interface AnwaltAuswahlData {
  anwaltId: string;
  anwaltKontaktModus: AnwaltKontaktModus;
}

interface Props {
  onComplete: (anwaltId: string, anwaltName: string, kontaktModus: AnwaltKontaktModus) => void;
  onBack: () => void;
}

const BRAND_BLUE = '#1E5FB7';

const FALLBACK_ANWAELTE: AnwaltOption[] = [
  { id: 'fallback-sturm',     name: 'Mirco Sturm',        kanzlei: 'Kanzlei Sturm & Partner' },
  { id: 'fallback-taudien',   name: 'Taudien',             kanzlei: 'Rechtsanwalt Taudien' },
  { id: 'fallback-schlechter',name: 'Norbert Schlechter',  kanzlei: 'Kanzlei Schlechter' },
];

const KONTAKT_MODI: { id: Exclude<AnwaltKontaktModus, ''>; label: string; subtext: string; icon: React.ReactNode; empfohlen?: boolean }[] = [
  {
    id: 'telefon',
    label: 'Anwalt soll mich telefonisch besprechen',
    subtext: 'Bevor er die Versicherung anschreibt',
    icon: <Phone size={22} />,
  },
  {
    id: 'direkt',
    label: 'Direkt loslegen — Versicherung anschreiben',
    subtext: 'Bei klarer Sachlage',
    icon: <Zap size={22} />,
    empfohlen: true,
  },
  {
    id: 'beide',
    label: 'Beide — erst kurz anrufen, dann schreiben',
    subtext: 'Persönliche Beratung + direkte Bearbeitung',
    icon: <MessageSquare size={22} />,
  },
];

// ─── Exported pure function for unit tests ────────────────────────────────────

export function canProceedAnwalt(data: AnwaltAuswahlData): boolean {
  return data.anwaltId !== '' && data.anwaltKontaktModus !== '';
}

// ─── Anwalt card ──────────────────────────────────────────────────────────────

function AnwaltKarte({
  anwalt,
  selected,
  onSelect,
}: {
  anwalt: AnwaltOption;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      onClick={onSelect}
      className="flex flex-col items-center gap-2 p-3 rounded-2xl border-2 transition-all text-center"
      style={{
        minHeight: 100,
        borderColor: selected ? BRAND_BLUE : '#E5E7EB',
        background: selected ? '#EFF6FF' : '#FFFFFF',
        flex: '1 1 0',
      }}
    >
      <div
        className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
        style={{ background: selected ? BRAND_BLUE : '#F3F4F6' }}
      >
        <User size={22} style={{ color: selected ? '#FFFFFF' : '#6B7280' }} />
      </div>
      <div>
        <div
          className="text-[13px] font-bold leading-tight"
          style={{ color: selected ? BRAND_BLUE : '#111827' }}
        >
          {anwalt.name}
        </div>
        <div className="text-[11px] text-gray-400 mt-0.5 leading-tight">{anwalt.kanzlei}</div>
      </div>
      {selected && (
        <div
          className="text-[10px] font-bold px-2 py-0.5 rounded-full"
          style={{ background: BRAND_BLUE, color: '#FFFFFF' }}
        >
          ✓ Gewählt
        </div>
      )}
    </button>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function AnwaltAuswahlStep({ onComplete, onBack }: Props) {
  const [top3, setTop3] = useState<AnwaltOption[]>(FALLBACK_ANWAELTE);
  const [alleAnwaelte, setAlleAnwaelte] = useState<AnwaltOption[]>([]);
  const [sucheModus, setSucheModus] = useState(false);
  const [suchtext, setSuchtext] = useState('');
  const [anwaltId, setAnwaltId] = useState('');
  const [anwaltName, setAnwaltName] = useState('');
  const [kontaktModus, setKontaktModus] = useState<AnwaltKontaktModus>('');

  useEffect(() => {
    fetch('/api/anwalt-partner?top=3')
      .then(r => r.json())
      .then((data: { anwaelte?: AnwaltOption[]; alle?: AnwaltOption[] }) => {
        if (data.anwaelte?.length) setTop3(data.anwaelte);
        if (data.alle?.length) setAlleAnwaelte(data.alle);
      })
      .catch(() => {
        // Fallback already set in initial state
      });
  }, []);

  const waehleAnwalt = useCallback((opt: AnwaltOption) => {
    setAnwaltId(opt.id);
    setAnwaltName(opt.name);
    setSucheModus(false);
    setSuchtext('');
  }, []);

  const gefilterteAnwaelte = alleAnwaelte.length > 0
    ? alleAnwaelte.filter(a =>
        a.name.toLowerCase().includes(suchtext.toLowerCase()) ||
        a.kanzlei.toLowerCase().includes(suchtext.toLowerCase()),
      )
    : FALLBACK_ANWAELTE.filter(a =>
        a.name.toLowerCase().includes(suchtext.toLowerCase()) ||
        a.kanzlei.toLowerCase().includes(suchtext.toLowerCase()),
      );

  const canGoNext = canProceedAnwalt({ anwaltId, anwaltKontaktModus: kontaktModus });

  const handleWeiter = () => {
    if (!canGoNext) return;
    onComplete(anwaltId, anwaltName, kontaktModus);
  };

  return (
    <div className="flex flex-col gap-4" style={{ maxHeight: 640 }}>

      {/* ── Schritt 10: Anwalt-Auswahl ── */}
      <div className="bg-white rounded-2xl border-2 border-gray-100 shadow-sm p-4">
        <div className="mb-3">
          <h2 className="text-[17px] font-bold text-gray-900">⚖️ Anwalt-Auswahl</h2>
          <p className="text-[12px] text-gray-500 mt-0.5 leading-relaxed">
            Damit Sie nichts selbst regeln müssen, übernimmt ein Anwalt aus unserem Netzwerk
            die Korrespondenz mit der Versicherung — komplett für Sie kostenfrei.
          </p>
        </div>

        {!sucheModus ? (
          <>
            <div className="flex gap-2">
              {top3.map(a => (
                <AnwaltKarte
                  key={a.id}
                  anwalt={a}
                  selected={anwaltId === a.id}
                  onSelect={() => waehleAnwalt(a)}
                />
              ))}
            </div>

            <button
              onClick={() => setSucheModus(true)}
              className="mt-2 w-full flex items-center justify-center gap-2 py-2 rounded-xl border-2 border-dashed border-gray-300 hover:border-[#1E5FB7] hover:bg-blue-50 transition-all text-[13px] font-semibold text-gray-500 hover:text-[#1E5FB7]"
              style={{ minHeight: 40 }}
            >
              <Search size={14} />
              Anderen Anwalt wählen
              <ChevronDown size={14} />
            </button>

            <div className="mt-2 flex items-start gap-1.5 text-[11px] text-gray-400">
              <span className="mt-0.5 flex-shrink-0">ℹ️</span>
              <span>
                <strong className="text-gray-500">Regulierungsbeauftragter:</strong> Spart Zeit — die Versicherung bekommt einen festen Ansprechpartner.
              </span>
            </div>
          </>
        ) : (
          <div className="space-y-2">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                autoFocus
                value={suchtext}
                onChange={e => setSuchtext(e.target.value)}
                placeholder="Anwalt oder Kanzlei suchen…"
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border-2 border-[#1E5FB7] bg-white text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E5FB7]/30 transition-all"
                style={{ minHeight: 44 }}
              />
            </div>

            <div className="max-h-[140px] overflow-y-auto space-y-1">
              {gefilterteAnwaelte.length === 0 ? (
                <p className="text-[12px] text-gray-400 text-center py-3">Keine Ergebnisse</p>
              ) : (
                gefilterteAnwaelte.map(a => (
                  <button
                    key={a.id}
                    onClick={() => waehleAnwalt(a)}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-blue-50 transition-all text-left"
                    style={{
                      minHeight: 44,
                      background: anwaltId === a.id ? '#EFF6FF' : undefined,
                      borderLeft: anwaltId === a.id ? `3px solid ${BRAND_BLUE}` : '3px solid transparent',
                    }}
                  >
                    <div className="w-8 h-8 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <User size={15} className="text-gray-500" />
                    </div>
                    <div>
                      <div className="text-[13px] font-semibold text-gray-900">{a.name}</div>
                      <div className="text-[11px] text-gray-400">{a.kanzlei}</div>
                    </div>
                  </button>
                ))
              )}
            </div>

            <button
              onClick={() => { setSucheModus(false); setSuchtext(''); }}
              className="w-full flex items-center justify-center gap-1.5 py-1.5 text-[12px] text-gray-400 hover:text-gray-600 transition-colors"
            >
              <ChevronUp size={13} />
              Zurück zu den Top-3
            </button>
          </div>
        )}
      </div>

      {/* ── Schritt 11: Kommunikationsmodus ── */}
      <div className="bg-white rounded-2xl border-2 border-gray-100 shadow-sm p-4">
        <h2 className="text-[17px] font-bold text-gray-900 mb-2">💬 Wie soll der Anwalt vorgehen?</h2>
        <div className="flex gap-2">
          {KONTAKT_MODI.map(m => (
            <button
              key={m.id}
              onClick={() => setKontaktModus(m.id)}
              className="flex flex-col items-center gap-1.5 p-3 rounded-2xl border-2 transition-all text-center relative"
              style={{
                flex: '1 1 0',
                minHeight: 110,
                borderColor: kontaktModus === m.id ? BRAND_BLUE : '#E5E7EB',
                background: kontaktModus === m.id ? '#EFF6FF' : '#FAFAFA',
              }}
            >
              {m.empfohlen && (
                <div
                  className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap"
                  style={{ background: '#F59E0B', color: '#FFFFFF' }}
                >
                  Empfohlen
                </div>
              )}
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{
                  background: kontaktModus === m.id ? BRAND_BLUE : '#F3F4F6',
                  color: kontaktModus === m.id ? '#FFFFFF' : '#6B7280',
                }}
              >
                {m.icon}
              </div>
              <div
                className="text-[12px] font-bold leading-tight"
                style={{ color: kontaktModus === m.id ? BRAND_BLUE : '#374151' }}
              >
                {m.label}
              </div>
              <div className="text-[10px] text-gray-400 leading-tight">{m.subtext}</div>
            </button>
          ))}
        </div>
      </div>

      {/* ── Navigation ── */}
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-6 py-3 rounded-xl border-2 border-gray-200 hover:bg-gray-50 transition-all text-[14px] font-semibold text-gray-600"
          style={{ minHeight: 52 }}
        >
          <ArrowLeft size={16} />
          Zurück
        </button>
        <button
          onClick={handleWeiter}
          disabled={!canGoNext}
          className="flex items-center gap-2 px-7 py-3 rounded-xl text-[14px] font-bold text-white shadow-md hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ background: `linear-gradient(135deg, ${BRAND_BLUE}, #1A4F9A)`, minHeight: 52 }}
        >
          Weiter
          <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}
